import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Page } from '@app/app/shared/models/page';
import { RestClientService } from '@app/app/shared/services/rest-client.service';
import { ObservableResult } from '@app/app/shared/store';
import { environment } from '@app/environments/environment';
import { catchError, switchMap } from 'rxjs/operators';
import { RegistrationDecision, RegistrationDecisionResult, RegistrationRequest, RegistrationsQuery } from './registration.model';
import { RegistrationsDataService } from './registrations-data.service';

const REGISTRATIONS_URL = Location.joinWithSlash(environment.apiRootUrl || '', 'registrations');

@Injectable()
export class ApiRegistrationsDataService implements RegistrationsDataService {
  constructor(private client: RestClientService) {}

  list(query: RegistrationsQuery): ObservableResult<Page<RegistrationRequest>> {
    const params: Record<string, string> = {
      status: query.statuses.join(','),
      page: query.page.toString(),
      size: query.size.toString(),
    };
    if (query.contentUid) {
      params['contentUid'] = query.contentUid;
    }
    if (query.requestedFrom) {
      params['requestedFrom'] = startOfDay(query.requestedFrom);
    }
    if (query.requestedTo) {
      params['requestedTo'] = endOfDay(query.requestedTo);
    }
    return this.client.get<Page<RegistrationRequest>>(REGISTRATIONS_URL, params).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError((err: HttpErrorResponse) => ObservableResult.ofError(apiMessage(err))),
    );
  }

  decide(enrolmentUids: string[], decision: RegistrationDecision, reason?: string): ObservableResult<RegistrationDecisionResult[]> {
    return this.client
      .post<RegistrationDecisionResult[]>(Location.joinWithSlash(REGISTRATIONS_URL, 'decisions'), {
        enrolmentUids,
        decision,
        ...(reason ? { reason } : {}),
      })
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body ?? [])),
        catchError((err: HttpErrorResponse) => ObservableResult.ofError(apiMessage(err))),
      );
  }
}

/** Days are picked in the admin's own time zone. */
function startOfDay(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year, month - 1, day).toISOString();
}

function endOfDay(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year, month - 1, day, 23, 59, 59, 999).toISOString();
}

function apiMessage(err: HttpErrorResponse): string {
  return err?.status === 400 && err.error?.message ? err.error.message : '';
}
